'use client';

import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { bootstrapGuest, realAuthDeps } from '@/lib/auth';
import { track } from '@/lib/analytics/events';
import GoalPick from './GoalPick';
import Flyover from './Flyover';
import './onboarding.css';

/**
 * World-flavored strings resolve server-side (build time) by page.tsx and
 * pass in as props, matching TrainScreen's TrainStrings pattern (K14).
 */
export interface OnboardingStrings {
  route: string;
}

type Step = 'goal_pick' | 'flyover';

export default function OnboardingFlow({ strings }: { strings: OnboardingStrings }) {
  const router = useRouter();
  const [step, setStep] = useState<Step>('goal_pick');
  const guestStartedEmittedRef = useRef(false);

  const handleConfirm = useCallback(() => {
    if (!guestStartedEmittedRef.current) {
      guestStartedEmittedRef.current = true;
      track('guest_started', {});
    }
    // Fire-and-forget: warms the guest identity for /train's own bootstrap.
    // A failure here is never a gate — /train's AuthProvider owns retry/error
    // UI for guest bootstrap.
    void bootstrapGuest(realAuthDeps()).catch(() => {});
    setStep('flyover');
  }, []);

  const handleDone = useCallback(() => {
    router.push('/train');
  }, [router]);

  if (step === 'goal_pick') {
    return <GoalPick route={strings.route} onConfirm={handleConfirm} />;
  }

  return <Flyover onDone={handleDone} />;
}
